import React, { useEffect, useState } from "react";
import { Form, Button, message, Tag, Typography } from "antd";
import {
  morningKeys,
  afternoonKeys,
  eveningKeys,
  mergeDietData,
  customTag,
  transformData,
  midnightKeys,
} from "../nutritionistKeys";
import DietEditTable from "./DietEditTable";
import {
  deleteDietItems,
  updatePatientDietItemsApi,
} from "../../../../../services/nutritionist.services";
import useQuery from "../../../../../hooks/useQuery";
import btnStyles from "../../../../../styles/Custom/Button.module.css";
import { EyeOutlined } from "@ant-design/icons";
import {
  getLabelTagsWithChecklistLables,
} from "../../../../IpEMR/InspectionSummaries/constructTextFromValues";
import { otherSpecificationsOptions } from "../../../../IpEMR/Options/dietAdviceOptions";
import Input from "antd/es/input/Input";

const DietEditChart = ({
  dietEditData,
  refresh,
  setEditState,
  dietDetails,
}) => {
  const params = useQuery();
  const [form] = Form.useForm();
  const keyData = [
    ...midnightKeys,
    ...morningKeys,
    ...afternoonKeys,
    ...eveningKeys,
  ];
  const diet = [
    dietEditData?.slots?.mid_night || [],
    dietEditData?.slots?.morning || [],
    dietEditData?.slots?.afternoon || [],
    dietEditData?.slots?.evening || [],
  ].flat();
  // const [tableData, setTableData] = useState(mergeDietData(keyData, diet));
  // console.log(keyData, diet);
  const mergedData = mergeDietData(keyData, diet);
  // console.log(mergedData);
  const [dietData, setDietData] = useState(mergedData);
  // console.log(dietData);
  const isActive = params?.status === "current";

  const handleTableChange = (updatedData) => {
    setDietData(updatedData);
  };
  useEffect(() => {
    if (dietEditData?.comments) {
      form.setFieldsValue({ comments: dietEditData?.comments });
    }
  }, [form, dietEditData?.comments]);

  const calculateTotals = (data) => {
    if (!data || data.length === 0) {
      return {
        carbohydrates: 0,
        proteins: 0,
        fats: 0,
        calories: 0,
        sodium: 0,
        potassium: 0,
      };
    }
    return data?.reduce(
      (totals, item) => ({
        proteins: (totals.proteins || 0) + (item.proteins || 0),
        calories: (totals.calories || 0) + (item.calories || 0),
        sodium: (totals.sodium || 0) + (item.sodium || 0),
        potassium: (totals.potassium || 0) + (item.potassium || 0),
        carbohydrates: (totals.carbohydrates || 0) + (item.carbohydrates || 0),
      }),
      {}
    );
  };

  const getUnits = (key) =>
    ["proteins", "carbohydrates"]?.includes(key)
      ? "g"
      : key === "calories"
      ? "kcal"
      : "mg";

  const mapToCustomTag = (data, color) => {
    return Object.entries(data).map(([key, value]) =>
      customTag(
        key === "calories" ? "energy" : key,
        value,
        color,
        getUnits(key)
      )
    );
  };

  const totals = calculateTotals(dietData);

  const combinedTotals = {
    calories: totals.calories,
    proteins: totals.proteins,
    carbohydrates: totals.carbohydrates,
    sodium: totals.sodium,
    potassium: totals.potassium,
  };

  async function onFinish(values) {
    // console.log(values);
    const removed = removeUnmatchedDietItems(values, diet);
    // console.log(removed);
    const payload = {
      comments: values.comments || "",
      diet_items: transformData(dietData),
      additional_info: {
        diet_items: combinedTotals,
        nutritional_recommendations:
          dietDetails?.nutritional_recommendations ??
          dietDetails?.ia?.nutritional_recommendations,
        otherSpecifications:
          dietDetails?.otherSpecifications ??
          dietDetails?.ia?.otherSpecifications,
        dietType: dietDetails?.dietType ?? dietDetails?.ia?.dietType,
      },
    };
    // console.log(payload);
    if (removed?.length > 0) {
      deleteDietItems(params?.patient, removed)
        ?.then((res) => {
          message.success(res?.data?.message);
          updatePatientDietItemsApi(params?.patient, payload)
            .then((res) => {
              // message.success(res?.data?.message);
              refresh();
              setEditState(false);
            })
            .catch((err) => console.log(err?.response?.data?.message));
        })
        .catch((err) => console.log(err));
    } else {
      updatePatientDietItemsApi(params?.patient, payload)
        .then((res) => {
          message.success(res?.data?.message);
          refresh();
          setEditState(false);
        })
        .catch((err) => console.log(err?.response?.data?.message));
    }
  }

  function removeUnmatchedDietItems(formData, dietData) {
    let result = {};

    // Iterate over each time key in formData
    Object.keys(formData).forEach((timeKey) => {
      const formSlotData = formData[timeKey];

      // Iterate over each slot in dietData

      const dietSlotData = diet;

      if (Array.isArray(dietSlotData)) {
        // Find the matching time in the corresponding dietData slot
        const secondTimeSlot = dietSlotData.find(
          (slot) => slot.time === parseInt(timeKey.split(":")[0], 10)
        );

        if (secondTimeSlot) {
          // Get the diet__id values from formData and dietData for the current slotType
          const firstDietIds = formSlotData?.items || [];

          // Find the ids that are in dietData but not in formData
          const removedDietIds = secondTimeSlot.items.filter(
            (item) => !firstDietIds.includes(item.diet__id)
          );

          // If there are removed diet ids, store them in the result
          if (removedDietIds.length > 0) {
            result[timeKey] = result[timeKey] || {}; // Initialize if not already
            result[timeKey] = removedDietIds; // Store removed items
          }
        }
      }
    });

    // Flatten the result and return the IDs of removed items
    return Object.values(result).flatMap((timeData) =>
      Object.values(timeData)
        .flat()
        .map((item) => item.id)
    );
  }

  const filterOnDietType = (data) => {
    if ((dietDetails?.dietType ?? dietDetails?.ia?.dietType) === "Oral") {
      return Object.fromEntries(
        Object.entries(data).filter(([key]) =>
          ["proteins", "protein", "calories"].includes(key)
        )
      );
    }
    return data;
  };

  return (
    <Form form={form} onFinish={onFinish} className="diet-edit-chart-root">
      <DietEditTable
        titleData={() => {
          const dietTotals = combinedTotals;
          const required =
            dietDetails?.nutritional_recommendations ??
            dietDetails?.ia?.nutritional_recommendations;
          const specTags = getLabelTagsWithChecklistLables(
            otherSpecificationsOptions,
            dietDetails?.otherSpecifications ??
              dietDetails?.ia?.otherSpecifications
          );

          return (
            <div className="diet-edit-chart-header">
              <div className="diet-edit-chart-header__main">
                {(dietDetails?.dietType || dietDetails?.ia?.dietType) && (
                  <Typography
                    color="purple"
                    className="diet-edit-chart-diet-type"
                  >
                    Diet Type:{" "}
                    <Typography.Text className="diet-edit-chart-diet-type__value">
                      {dietDetails?.dietType || dietDetails?.ia?.dietType}
                    </Typography.Text>
                  </Typography>
                )}
                {specTags ? (
                  <div className="diet-edit-chart-spec-tags">{specTags}</div>
                ) : null}
                <div className="diet-edit-chart-tags">
                  {(() => {
                    if (!required) {
                      return (
                        <>Diet Items {mapToCustomTag(dietTotals, "purple")}</>
                      );
                    }

                    const combinedTags = [];

                    if (
                      dietTotals.calories !== undefined &&
                      required.calories !== undefined
                    ) {
                      combinedTags.push(
                        <Tag
                          key="energy"
                          color="purple"
                          style={{ fontWeight: 600 }}
                        >
                          Energy - {dietTotals.calories}/{required.calories}{" "}
                          kcal
                        </Tag>
                      );
                    }

                    if (
                      dietTotals.proteins !== undefined &&
                      required.protein !== undefined
                    ) {
                      combinedTags.push(
                        <Tag
                          key="proteins"
                          color="purple"
                          style={{ fontWeight: 600 }}
                        >
                          Proteins - {dietTotals.proteins}/{required.protein} g
                        </Tag>
                      );
                    }
                    if (
                      dietTotals?.carbohydrates !== undefined &&
                      required?.carbohydrates !== undefined
                    ) {
                      combinedTags.push(
                        <Tag
                          key="carbs"
                          color="purple"
                          style={{ fontWeight: 600 }}
                        >
                          Carbs - {dietTotals?.carbohydrates}/
                          {required?.carbohydrates} g
                        </Tag>
                      );
                    }
                    if (
                      dietTotals?.sodium !== undefined &&
                      required?.sodium !== undefined
                    ) {
                      combinedTags.push(
                        <Tag
                          key="sodium"
                          color="purple"
                          style={{ fontWeight: 600 }}
                        >
                          Sodium - {dietTotals?.sodium}/{required?.sodium} mg
                        </Tag>
                      );
                    }
                    if (
                      dietTotals?.potassium !== undefined &&
                      required?.potassium !== undefined
                    ) {
                      combinedTags.push(
                        <Tag
                          key="potassium"
                          color="purple"
                          style={{ fontWeight: 600 }}
                        >
                          Potassium - {dietTotals?.potassium}/
                          {required?.potassium} mg
                        </Tag>
                      );
                    }

                    return <>{combinedTags}</>;
                  })()}
                </div>
                <Form.Item
                  name="comments"
                  label="Comment"
                  className="diet-edit-chart-comment"
                >
                  <Input.TextArea
                    placeholder="Enter comment"
                    autoSize={{ minRows: 2, maxRows: 4 }}
                  />
                </Form.Item>
              </div>
              <EyeOutlined
                onClick={() => setEditState(false)}
                className="diet-edit-chart-close"
              />
            </div>
          );
        }}
        data={mergedData}
        form={form}
        refresh={refresh}
        onChange={handleTableChange}
        dietType={dietDetails?.dietType ?? dietDetails?.ia?.dietType}
        dietDetails={dietDetails}
        combinedTotals={combinedTotals}
      />

      {isActive && (
        <Form.Item className="diet-edit-chart-submit">
          <Button type="primary" htmlType="submit" className={btnStyles.mdBtn}>
            Submit
          </Button>
        </Form.Item>
      )}
    </Form>
  );
};

export default DietEditChart;
